import React from 'react';
import QrCode from 'react-qr-code';

import format from 'date-fns/format';

import { Container } from '@ott/grid';
import IconOneWayRoute from '@ott/icon-arrow-right';
import { useLayoutContext } from '@ott/layout-context';
import sendKM from '@ott/utility-km';

import FadeIn from 'src/components/elements/FadeIn';
import { PhoneFrame } from 'src/components/elements/PhoneFrame';

import { getMobileDevicePlatform } from 'src/helpers/common';
import { getIsMobileUserAgent } from 'src/helpers/getIsMobileUserAgent';
import useOrdersServiceSharingInfo from 'src/hooks/useOrdersServiceSharingInfo';

import { S3_URL, LOGO, PRODUCTS } from './constants';
import { getQRCodeLink } from './utils/getQrCodeLink';
import { isRoundTrip } from './utils/isRoundTrip';
import { omitTransitFlights } from './utils/omitTransitFlights';

import styles from './SharingPage.scss';

const isMobileUserAgent = getIsMobileUserAgent(
  typeof window !== 'undefined' ? window.navigator.userAgent : ''
);

const qrCodeLink = getQRCodeLink();

const devicePlatform = getMobileDevicePlatform();

const handleDownloadAppButtonClick = () => {
  sendKM('DOWNLOAD_BUTTON_CLICK', {
    obj: {
      source: window.location.pathname,
      devicePlatform,
    },
  });
  window.open(qrCodeLink, '_blank');
};

const SharingPage = () => {
  const { isLoading, result } = useOrdersServiceSharingInfo();

  const { isDesktop } = useLayoutContext();

  const getRouteIcon = () => {
    if (result?.order.product === PRODUCTS.HOTELS) {
      return <img src={`${S3_URL}sharing_landing/hotels_icon.svg`} alt="hotels-icon" />;
    }

    if (result?.order.product === PRODUCTS.AVIA) {
      return <img src={`${S3_URL}sharing_landing/avia_icon.svg`} alt="avia-icon" />;
    }

    return null;
  };

  const getRouteInfo = () => {
    if (result?.order) {
      const { product, rentInfo, trips = [] } = result.order;

      if (product === PRODUCTS.HOTELS && rentInfo) {
        return (
          <>
            <div className={styles.route}>{rentInfo.hotel}</div>
            <div className={styles.date}>
              {format(new Date(rentInfo.arrivalDate), 'd')}
              &nbsp;-&nbsp;
              {format(new Date(rentInfo.departureDate), 'd MMMM')}
            </div>
          </>
        );
      }

      if (product === PRODUCTS.AVIA && trips.length !== 0) {
        const directFlights = omitTransitFlights(trips);
        const isRt = isRoundTrip(directFlights.locations);
        const locations = isRt
          ? directFlights.locations.slice(0, Math.floor(directFlights.locations.length / 2))
          : directFlights.locations;

        return (
          <>
            <div className={styles.route}>
              {locations.map((location, index) => (
                <React.Fragment key={location}>
                  {isRt && index !== 0 && (
                    <img src={`${S3_URL}sharing_landing/directions_icon.svg`} alt="directions" />
                  )}
                  {!isRt && index !== 0 && <IconOneWayRoute className={styles.iconRoute} />}
                  {location}
                </React.Fragment>
              ))}
            </div>
            <div className={styles.date}>
              {directFlights.dates.map((date, index) => (
                <React.Fragment key={date}>
                  {index !== 0 && ' – '}
                  {format(new Date(date.slice(0, 10)), 'd MMMM, EEEE')}
                </React.Fragment>
              ))}
            </div>
          </>
        );
      }
    }

    return null;
  };

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <>
      <div className={styles.header}>
        <a href="https://solartrip.com/" className={styles.logoLink}>
          <img className={styles.logo} src={isDesktop ? LOGO.DESKTOP : LOGO.MOBILE} alt="logo" />
        </a>
      </div>
      <Container className={styles.container}>
        <div className={styles.row}>
          <div className={styles.columnContent}>
            <FadeIn delay={0.2}>
              <h1 className={styles.title}>
                You&rsquo;re invited
                <br />
                to&nbsp;join <span className={styles.titleThin}>a&nbsp;Solartrip</span>
                <br />
                <span className={styles.titleThin}>shared booking</span>
              </h1>
            </FadeIn>
            <FadeIn delay={0.4}>
              <p className={styles.description}>
                Download or update the app on&nbsp;your mobile device and click on&nbsp;the link
                provided earlier to&nbsp;view the booking
              </p>
            </FadeIn>

            {isLoading ? (
              <div className={styles.routeSkeleton} />
            ) : (
              <FadeIn className={getRouteInfo() ? styles.routeInfo : ''} delay={0}>
                {isDesktop && getRouteIcon()}
                <div className={styles.routeWrapper}>{getRouteInfo()}</div>
              </FadeIn>
            )}

            {isMobileUserAgent && (
              <button
                type="button"
                className={styles.storeIconWrapper}
                onClick={handleDownloadAppButtonClick}
              >
                {devicePlatform === 'android' && (
                  <img
                    src={`${S3_URL}app_landing/download_block/google_play.svg`}
                    alt="google-play"
                  />
                )}
                {devicePlatform === 'ios' && (
                  <img src={`${S3_URL}app_landing/download_block/appstore.svg`} alt="appstore" />
                )}
              </button>
            )}
          </div>

          {!isMobileUserAgent && (
            <div className={styles.columnCode}>
              <PhoneFrame>
                <div className={styles.phoneContent}>
                  <img className={styles.phoneLogo} src={LOGO.DESKTOP} alt="logo" />
                  <QrCode value={qrCodeLink} className={styles.qrCode} />
                </div>
              </PhoneFrame>
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

export default SharingPage;
